import { useEffect, useMemo, useState } from "react";
import { ArrowLeft, X, Plus, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import api from "../../../lib/api";
import { formatPrice } from "../../../lib/formatPrice";
import LabBookingLayout from "../components/LabBookingLayout";
import { useLabBooking } from "../context/LabBookingContext";

// Same composite score used on the Flutter Compare Labs page:
// score = total price + (distance_km * ₹10 travel cost). Lowest score wins,
// only fully-matched labs are eligible.
const TRAVEL_COST_PER_KM = 10;

// One (lab, variant test) pairing — same layout/fields as mobile's
// _buildAddonCard. Deliberately shows just this one test's price as the
// subtotal/total (a preview of the swap), not a full recomputed cart total —
// the actual swap-aware test list is built in handleBookAddon on click.
const AddonCard = ({ lab, test, collectionType, isBooking, disabled, error, onBook }) => {
  const homeCharge = lab.home_collection_charge;
  const finalPrice = Number(test.final_price ?? test.price);
  return (
    <div className="bg-white rounded-2xl p-5 border border-gray-100">
      <p className="text-[14px] font-bold text-[#191C1D]">{lab.lab_name}</p>
      <p className="text-[11px] text-[#6B7A99] mt-0.5">
        {lab.lab_area}
        {lab.lab_distance != null ? ` · ${Math.round(lab.lab_distance / 1000)} km` : ""}
      </p>
      {lab.lab_address && (
        <p className="text-[11px] text-[#6B7A99] mt-0.5 line-clamp-2">{lab.lab_address}</p>
      )}
      <div className="flex flex-wrap gap-1.5 mt-2">
        {lab.home_collection_available && (
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#E6F5F1] text-[#13B58E] font-medium">Home Collection</span>
        )}
        {lab.lab_visit_available && (
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#E6F0FB] text-[#191C1D] font-medium">Lab Visit</span>
        )}
        {Number(lab.lab_discount) > 0 && (
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#FFF1E6] text-[#E07A1F] font-medium">
            {formatPrice(lab.lab_discount)}% Discount
          </span>
        )}
      </div>
      <div className="mt-3 flex justify-between items-baseline gap-2 text-[12px]">
        <span className="text-[#6B7A99] truncate">{test.name}</span>
        <span className="font-semibold text-[#191C1D] shrink-0">₹{formatPrice(finalPrice)}</span>
      </div>
      <div className="mt-2 pt-2 border-t border-gray-100 space-y-1.5">
        <div className="flex justify-between text-[12px] text-[#6B7A99]">
          <span>Tests Subtotal</span>
          <span className="font-semibold text-[#191C1D]">₹{formatPrice(finalPrice)}</span>
        </div>
        {lab.home_collection_available && collectionType === "home" && (
          <div className="flex justify-between text-[12px] text-[#6B7A99]">
            <span>Home Collection</span>
            <span className={homeCharge === 0 ? "text-[#13B58E] font-semibold" : ""}>
              {homeCharge === null || homeCharge === undefined ? "—" : Number(homeCharge) > 0 ? `+₹${formatPrice(homeCharge)}` : "Free"}
            </span>
          </div>
        )}
        <div className="flex justify-between items-center pt-1 border-t border-gray-50">
          <span className="text-[13px] font-bold text-[#191C1D]">Total</span>
          <span className="text-[14px] font-extrabold text-[#13B58E]">
            ₹{formatPrice(finalPrice + (collectionType === "home" ? Number(homeCharge || 0) : 0))}
          </span>
        </div>
      </div>
      <button
        onClick={onBook}
        disabled={disabled}
        className="w-full mt-3 bg-[#13B58E] text-white text-[13px] font-semibold py-2.5 rounded-full hover:bg-[#0fa07d] transition disabled:opacity-60"
      >
        {isBooking ? <Loader2 size={14} className="animate-spin inline" /> : "Book"}
      </button>
      {error && <p className="text-[12px] text-red-500 mt-2">{error}</p>}
    </div>
  );
};

const CompareLabsPage = () => {
  const navigate = useNavigate();
  const {
    selectedTests,
    removeTest,
    setSelectedLab,
    location,
    locationStatus,
    requestLocation,
    collectionType,
    setCollectionType,
    user,
    pendingLabBooking,
    setPendingLabBooking,
  } = useLabBooking();

  const [labs, setLabs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => { requestLocation(); }, [requestLocation]);

  const testMasterIds = selectedTests.filter((t) => !t.is_package && !t.is_unmapped).map((t) => t.test_master_id);
  const testNames     = selectedTests.filter((t) => t.is_unmapped).map((t) => t.name);
  const packageNames  = selectedTests.filter((t) => t.is_package).map((t) => t.name);

  const collectionTypeNum = collectionType === "lab" ? 1 : 2;

  useEffect(() => {
    if (!location || (testMasterIds.length === 0 && testNames.length === 0 && packageNames.length === 0)) {
      setLabs([]);
      return;
    }
    setLoading(true);
    setError(null);
    api
      .post("/user/lab/compare", {
        test_master_ids: testMasterIds.join(","),
        test_names:      testNames.join(","),
        package_names:   packageNames.join(","),
        collection_type: collectionTypeNum,
      })
      .then((res) => setLabs(res.data?.data?.labs || []))
      .catch(() => setError("Couldn't load labs, try again."))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, collectionTypeNum, testMasterIds.join(","), testNames.join(","), packageNames.join(",")]);

  const recommendedLabId = useMemo(() => {
    let bestId = null, bestScore = null;
    for (const lab of labs) {
      if (lab.matched_count !== lab.total_count) continue;
      const charge = collectionType === "home" ? Number(lab.home_collection_charge || 0) : 0;
      // Home collection: distance irrelevant — phlebotomist comes to user.
      // Lab visit: add distance penalty so closer labs rank higher.
      const distPenalty = collectionType === "home" ? 0 : (lab.lab_distance || 0) / 1000 * TRAVEL_COST_PER_KM;
      const score = Number(lab.total_amount) + charge + distPenalty;
      if (bestScore === null || score < bestScore) { bestScore = score; bestId = lab.lab_id; }
    }
    return bestId;
  }, [labs, collectionType]);

  // Recommended lab pinned to top; rest keep backend order.
  const orderedLabs = useMemo(() => {
    if (!recommendedLabId) return labs;
    const idx = labs.findIndex((l) => l.lab_id === recommendedLabId);
    if (idx <= 0) return labs;
    const reordered = [...labs];
    reordered.unshift(reordered.splice(idx, 1)[0]);
    return reordered;
  }, [labs, recommendedLabId]);

  // Flattened (lab, variantTest) pairs across all labs — price-variant
  // duplicates and add-on variants (is_variant 1), shown as standalone
  // "Also Available" cards below the main lab list, same as mobile.
  const addonEntries = useMemo(() => {
    const entries = [];
    for (const lab of orderedLabs) {
      for (const test of lab.tests || []) {
        if (test.is_variant) entries.push({ lab, test });
      }
    }
    return entries;
  }, [orderedLabs]);

  // Booking state keyed per-card, not per-lab — a lab can have both a main
  // card and several Also Available addon cards, each needing its own
  // independent spinner/error rather than all lighting up together.
  const [bookingKey, setBookingKey] = useState(null);
  const [bookErrors, setBookErrors] = useState({}); // { [key]: message }
  const mainCardKey  = (lab) => `${lab.lab_id}`;
  const addonCardKey = (lab, test) => `${lab.lab_id}:addon:${test.lab_test_id}`;

  // Shared by the main card's Book button and each Also Available addon
  // card's Book button — only the test_ids selection and state key differ.
  const bookTests = (lab, testIds, key) => {
    if (!user) {
      // Store what the user tried to book, complete cart call after login.
      setPendingLabBooking({ lab, testIds, collectionType });
      navigate("/lab-booking/login");
      return;
    }

    setBookingKey(key);
    setBookErrors((prev) => ({ ...prev, [key]: null }));

    api
      .post("/user/lab/cart", {
        lab_id: lab.lab_id,
        test_ids: testIds,
        test_collection_type: collectionTypeNum,
      })
      .then((res) => {
        if (res.data?.code === 200) {
          setSelectedLab({ lab: res.data.data.lab, testList: res.data.data.test_list, homeCollectionCharge: lab.home_collection_charge });
          navigate("/lab-booking/cart");
        } else {
          setBookErrors((prev) => ({
            ...prev,
            [key]: res.data?.message || "Couldn't add to cart, try again.",
          }));
        }
      })
      .catch(() =>
        setBookErrors((prev) => ({
          ...prev,
          [key]: "Couldn't add to cart, try again.",
        }))
      )
      .finally(() => setBookingKey(null));
  };

  const handleBook = (lab) => {
    // lab.tests includes price/add-on variants (is_variant 1) alongside the
    // actually-matched tests — only the matched ones should ever be booked.
    const testIds = (lab.tests || []).filter((t) => !t.is_variant).map((t) => t.lab_test_id).join(",");
    bookTests(lab, testIds, mainCardKey(lab));
  };

  // Booking an Also Available variant swaps it in for the one matched test
  // it replaces (slot_master_id tells us which) — same as
  // LabCompareLabsController._doBookAddonTest on mobile — plus every other
  // matched test from that lab, unaffected.
  const handleBookAddon = (lab, addonTest) => {
    const testIds = (lab.tests || [])
      .filter((t) => !t.is_variant && t.test_master_id !== addonTest.slot_master_id)
      .map((t) => t.lab_test_id)
      .concat(addonTest.lab_test_id != null ? [addonTest.lab_test_id] : [])
      .join(",");
    bookTests(lab, testIds, addonCardKey(lab, addonTest));
  };

  // If we just came back from login with a pendingLabBooking already cleared
  // (handled inside LoginPage), nothing special to do here.

  return (
    <LabBookingLayout>
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-1.5 text-[13px] font-semibold text-[#3D4945] hover:text-[#13B58E] mb-4"
      >
        <ArrowLeft size={16} /> Back
      </button>

      <h1 className="text-[22px] font-extrabold text-[#191C1D] mb-4">Compare Labs</h1>

      {/* Collection type toggle — same as mobile app, shown before lab results */}
      <div className="flex bg-[#F3F6F7] rounded-full p-1 mb-5 w-fit">
        <button
          onClick={() => setCollectionType("home")}
          className={`text-[13px] font-semibold px-5 py-2 rounded-full transition ${
            collectionType === "home" ? "bg-[#13B58E] text-white" : "text-[#6B7A99]"
          }`}
        >
          Home Collection
        </button>
        <button
          onClick={() => setCollectionType("lab")}
          className={`text-[13px] font-semibold px-5 py-2 rounded-full transition ${
            collectionType === "lab" ? "bg-[#13B58E] text-white" : "text-[#6B7A99]"
          }`}
        >
          Lab Visit
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-6">
        {selectedTests.length === 0 && (
          <span className="text-[12px] text-[#6B7A99]">No tests selected — add some from the home page.</span>
        )}
        {selectedTests.map((test) => (
          <span
            key={test.is_package ? `package:${test.name}` : test.is_unmapped ? `unmapped:${test.name}` : `test:${test.test_master_id}`}
            className="flex items-center gap-1.5 text-[12px] px-3 py-1.5 rounded-full border border-gray-200 bg-white text-[#3D4945]"
          >
            {test.name}
            <button onClick={() => removeTest(test)}><X size={12} /></button>
          </span>
        ))}
        <button
          onClick={() => navigate("/?section=lab-booking")}
          className="flex items-center gap-1 text-[12px] px-3 py-1.5 rounded-full border border-dashed border-[#13B58E] text-[#13B58E]"
        >
          <Plus size={12} /> Add
        </button>
      </div>

      {!location && (locationStatus === "idle" || locationStatus === "loading") && (
        <div className="flex items-center gap-2 text-[13px] text-[#6B7A99] py-6">
          <Loader2 size={16} className="animate-spin" /> Getting your location to find nearby labs...
        </div>
      )}
      {!location && locationStatus === "denied" && (
        <p className="text-[13px] text-[#B45309] py-6">
          Location access is needed to compare nearby labs.{" "}
          <button onClick={requestLocation} className="underline font-medium">Try again</button>
        </p>
      )}
      {!location && locationStatus === "unsupported" && (
        <p className="text-[13px] text-[#B45309] py-6">Your browser doesn't support location access.</p>
      )}
      {!!location && loading && (
        <div className="flex items-center gap-2 text-[13px] text-[#6B7A99] py-6">
          <Loader2 size={16} className="animate-spin" /> Finding nearby labs...
        </div>
      )}
      {!!location && !loading && error && (
        <p className="text-[13px] text-red-500 py-6">{error}</p>
      )}
      {!!location && !loading && !error && selectedTests.length > 0 && labs.length === 0 && (
        <p className="text-[13px] text-[#6B7A99] py-6">No labs near you offer these tests yet.</p>
      )}
      {!user && labs.length > 0 && (
        <p className="text-[12px] text-[#B45309] mb-3">You'll be asked to sign in when you click Book.</p>
      )}

      <div className="space-y-4 max-w-2xl">
        {!loading && !error && orderedLabs.map((lab) => {
          const isRecommended = lab.lab_id === recommendedLabId;
          // lab.tests mixes the actual matched tests (is_variant 0) with
          // price/add-on variants (is_variant 1) — variants are rendered
          // separately in the Also Available section below. Backend's own
          // total_amount already excludes variants; mrpTotal must match
          // that scope too, or the struck-through MRP silently included
          // all the variant prices too.
          const matchedTests  = (lab.tests || []).filter((t) => !t.is_variant);
          const mrpTotal      = matchedTests.reduce((sum, t) => sum + Number(t.price || 0), 0);
          const isBooking     = bookingKey === mainCardKey(lab);
          const labError      = bookErrors[mainCardKey(lab)];

          return (
            <div
              key={lab.lab_id}
              className={`bg-white rounded-2xl p-5 ${isRecommended ? "border-2 border-[#13B58E]" : "border border-gray-100"}`}
            >
              {isRecommended && (
                <span className="inline-block text-[11px] font-semibold text-[#13B58E] bg-[#EEF3F2] px-2.5 py-1 rounded-full mb-2">
                  ⭐ Recommended
                </span>
              )}
              <p className="text-[15px] font-bold text-[#191C1D]">{lab.lab_name}</p>
              <p className="text-[12px] text-[#6B7A99] mt-0.5">
                {lab.matched_count}/{lab.total_count} tests matched{lab.lab_area ? ` · ${lab.lab_area}` : ""}
                {lab.lab_distance != null ? ` · ${Math.round(lab.lab_distance / 1000)} km` : ""}
              </p>
              <div className="flex flex-wrap gap-1.5 mt-2">
                {matchedTests.slice(0, 4).map((t) => (
                  <span key={t.lab_test_id} className="text-[10px] px-2 py-0.5 rounded-full bg-[#EEF3F2] text-[#13B58E] font-medium">
                    {t.name}
                  </span>
                ))}
              </div>
              <div className="mt-4 border-t border-gray-100 pt-3 space-y-1.5">
                {/* Per-test rows */}
                {matchedTests.map((t) => {
                  const finalPrice = Number(t.final_price ?? t.price);
                  const mrp        = Number(t.price);
                  const hasDiscount = t.final_price != null && finalPrice < mrp;
                  return (
                    <div key={t.lab_test_id} className="flex justify-between items-baseline text-[12px] text-[#6B7A99]">
                      <span className="truncate pr-2">{t.name}</span>
                      <span className="shrink-0 flex items-baseline gap-1">
                        {hasDiscount && (
                          <span className="text-[11px] line-through text-[#C0C8C6]">₹{formatPrice(mrp)}</span>
                        )}
                        ₹{formatPrice(finalPrice)}
                      </span>
                    </div>
                  );
                })}
                {/* Subtotal */}
                <div className="flex justify-between items-baseline text-[12px] text-[#6B7A99] pt-1 border-t border-gray-50">
                  <span>Tests Subtotal</span>
                  <span className="flex items-baseline gap-1">
                    {mrpTotal > Number(lab.total_amount) && (
                      <span className="text-[11px] line-through text-[#C0C8C6]">₹{formatPrice(mrpTotal)}</span>
                    )}
                    ₹{formatPrice(lab.total_amount)}
                  </span>
                </div>
                {/* Home collection charge */}
                {lab.home_collection_available && collectionType === "home" && (
                  <div className="flex justify-between text-[12px] text-[#6B7A99]">
                    <span>Home Collection</span>
                    <span className={lab.home_collection_charge === 0 ? "text-[#13B58E] font-semibold" : ""}>
                      {lab.home_collection_charge === null || lab.home_collection_charge === undefined
                        ? "—"
                        : Number(lab.home_collection_charge) > 0
                        ? `+₹${formatPrice(lab.home_collection_charge)}`
                        : "Free"}
                    </span>
                  </div>
                )}
                {/* Total row — matches mobile style */}
                {(() => {
                  const collCharge = collectionType === "home" ? Number(lab.home_collection_charge || 0) : 0;
                  const finalTotal = Number(lab.total_amount) + collCharge;
                  const mrpWithCollection = mrpTotal + collCharge;
                  return (
                    <div className="flex justify-between items-center pt-1 border-t border-gray-100">
                      <div className="flex items-baseline gap-2">
                        <span className="text-[13px] font-bold text-[#191C1D]">Total</span>
                        {mrpTotal > Number(lab.total_amount) && (
                          <span className="text-[11px] text-[#9AA6A4] line-through">₹{formatPrice(mrpWithCollection)}</span>
                        )}
                      </div>
                      <span className="text-[15px] font-extrabold text-[#13B58E]">
                        ₹{formatPrice(finalTotal)}
                      </span>
                    </div>
                  );
                })()}
              </div>
              <button
                onClick={() => handleBook(lab)}
                disabled={!!bookingKey}
                className="w-full mt-3 bg-[#13B58E] text-white text-[13px] font-semibold py-2.5 rounded-full hover:bg-[#0fa07d] transition disabled:opacity-60"
              >
                {isBooking ? <Loader2 size={14} className="animate-spin inline" /> : "Book"}
              </button>
              {labError && <p className="text-[12px] text-red-500 mt-2">{labError}</p>}
            </div>
          );
        })}
      </div>

      {!loading && !error && addonEntries.length > 0 && (
        <div className="max-w-2xl mt-6">
          <p className="text-[13px] font-bold text-[#191C1D] mb-3">Also Available</p>
          <div className="space-y-4">
            {addonEntries.map(({ lab, test }) => {
              const key = addonCardKey(lab, test);
              return (
                <AddonCard
                  key={key}
                  lab={lab}
                  test={test}
                  collectionType={collectionType}
                  isBooking={bookingKey === key}
                  disabled={!!bookingKey}
                  error={bookErrors[key]}
                  onBook={() => handleBookAddon(lab, test)}
                />
              );
            })}
          </div>
        </div>
      )}
    </LabBookingLayout>
  );
};

export default CompareLabsPage;
